import React, { useEffect, useRef, useState } from "react";
import Push from "push.js";
import { reconnectEventChannel, useSse } from "../hooks/useSse";
import intl from "react-intl-universal";
import notification from "../../asset/notification.png";
import entryRest from "../../rest/entryRest";


export const EntryContext = React.createContext();

export const EntryProvider = props => {

    const addKeys = keyable => {
        if (keyable === undefined) {
            return;
        }
        let maxKey = 0;
        keyable.forEach(obj => {
            const curKey = obj.key ?? 0;
            maxKey = Math.max(maxKey, curKey);
        });
        const keySet = new Set();
        keyable.forEach(obj => {
            if (Object.prototype.hasOwnProperty.call(obj, "key")) {
                if (keySet.has(obj.key)) {
                    obj.key = ++maxKey;
                }
            } else {
                obj.key = ++maxKey;
            }
            keySet.add(obj.key);
        });
    };

    const initEntryKeys = rec => {
        addKeys(rec.sectors);
        rec.sectors?.forEach(sector => addKeys(sector.radios));
        return rec;
    };

    const [entry, setEntry] = useState(props.entry ? initEntryKeys(props.entry) : undefined);
    const [saving, setSaving] = useState(false);
    const entryRef = useRef(entry);

    const setEntryInternal = rec => {
        const recWithKeys = initEntryKeys(rec);
        const nextEntry = { ...recWithKeys };
        setEntry(nextEntry);
        entryRef.current = nextEntry;
    };

    const emptyEntry = () => {
        setEntryInternal({
            id: null,
            name: null,
            calcSetting: {
                imageSetting: {},
                calcAreas: [],
            },
            sectors: [],
            plans: [],
        });
    };

    useEffect(() => {
        entryRef.current = entry;
    }, [entry]);

    useSse("ENTRY_CALCULATIONS_COMPLETE", pushedEntry => {
        if (entry.id === pushedEntry.id) {
            Push.create("WWCluster", {
                body: intl.get("notification.calculations.complete", { entryName: entry.name }),
                icon: notification,
                onClick: function () {
                    window.focus();
                    this.close();
                }
            });
        }
    });

    useSse("ENTRY_CALCULATION_FAILURE", pushedEntry => {
        if (entry.id === pushedEntry.id) {
            Push.create("WWCluster", {
                body: intl.get("notification.calculations.failure", { entryName: entry.name }),
                icon: notification,
                onClick: function () {
                    window.focus();
                    this.close();
                }
            });
        }
    });

    useSse("UPDATE_ENTRY", pushedEntry => {
        if (entry.id === pushedEntry.id) {
            setEntryInternal(pushedEntry);
        }
    });

    // only the two fields change, the nested objects stay the same references - so no form input sees a new value
    useSse("UPDATE_ENTRY_STATE", pushedState => {
        if (entry.id === pushedState.id) {
            setEntryInternal({ ...entry, progress: pushedState.progress, queueOrder: pushedState.queueOrder });
        }
    });


    // On websocket issues, download entry to see any progress changes
    useSse(reconnectEventChannel, async () => {
        const isCalcInProgress = entryRef.current?.calcSetting?.calcAreas?.some(calcArea =>
            calcArea.calculations?.some(calculation => !calculation.resultAvailable)
        ) || false;
        if(isCalcInProgress) {
            const updated = await entryRest.getById(entry.id);
            setEntryInternal(updated);
        }
    });

    return (
        <EntryContext.Provider value={{
            entry,
            saving,
            emptyEntry,
            setEntry: setEntryInternal,
            getEntry: () => entryRef.current,
            setSaving,
        }}>
            {props.children}
        </EntryContext.Provider>
    );
};
